import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enable cookies on all requests
axios.defaults.withCredentials = true;

// Court Form Auto-Fill API (SC)
export const courtFormApi = {
  getDetails: (estateId) => axios.get(`${API}/estates/${estateId}/court/details`),
  updateDetails: (estateId, details) => axios.put(`${API}/estates/${estateId}/court/details`, details),
  listForms: (estateId) => axios.get(`${API}/estates/${estateId}/court/forms`),
  downloadUrl: (estateId, formId) => `${API}/estates/${estateId}/court/forms/${formId}/download`,
};

// Estate Messages API
export const messageApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/messages`),
  send: (estateId, body) => axios.post(`${API}/estates/${estateId}/messages`, { body }),
  sendWithFile: (estateId, body, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('body', body || '');
    return axios.post(`${API}/estates/${estateId}/messages/with-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  unreadCount: (estateId) => axios.get(`${API}/estates/${estateId}/messages/unread-count`),
  getSmsPrefs: () => axios.get(`${API}/messages/sms-prefs`),
  updateSmsPrefs: (prefs) => axios.put(`${API}/messages/sms-prefs`, prefs),
};

// Two-Factor Auth API
export const twoFactorApi = {
  status: () => axios.get(`${API}/auth/2fa/status`),
  setup: () => axios.post(`${API}/auth/2fa/setup`),
  enable: (code) => axios.post(`${API}/auth/2fa/enable`, { code }),
  disable: (password, code) => axios.post(`${API}/auth/2fa/disable`, { password, code }),
  regenerateRecoveryCodes: (code) => axios.post(`${API}/auth/2fa/recovery-codes`, { code }),
};

// Bank Import API (Stripe Financial Connections)
export const bankApi = {
  linkSession: (estateId) => axios.post(`${API}/estates/${estateId}/bank/link-session`),
  linkComplete: (estateId, sessionId) => axios.post(`${API}/estates/${estateId}/bank/link-complete`, { session_id: sessionId }),
  accounts: (estateId) => axios.get(`${API}/estates/${estateId}/bank/accounts`),
  sync: (estateId, accountId) => axios.post(`${API}/estates/${estateId}/bank/accounts/${accountId}/sync`),
  import: (estateId, accountId, items) => axios.post(`${API}/estates/${estateId}/bank/accounts/${accountId}/import`, { items }),
  disconnect: (estateId, accountId) => axios.delete(`${API}/estates/${estateId}/bank/accounts/${accountId}`)
};

// Estate API
export const estateApi = {
  list: () => axios.get(`${API}/estates`),
  get: (id) => axios.get(`${API}/estates/${id}`),
  create: (data) => axios.post(`${API}/estates`, data),
  update: (id, data) => axios.put(`${API}/estates/${id}`, data),
  delete: (id) => axios.delete(`${API}/estates/${id}`)
};

// Task API
export const taskApi = {
  list: (estateId, params = {}) => axios.get(`${API}/estates/${estateId}/tasks`, { params }),
  get: (estateId, taskId) => axios.get(`${API}/estates/${estateId}/tasks/${taskId}`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/tasks`, data),
  update: (estateId, taskId, data) => axios.put(`${API}/estates/${estateId}/tasks/${taskId}`, data),
  delete: (estateId, taskId) => axios.delete(`${API}/estates/${estateId}/tasks/${taskId}`)
};

// Asset API
export const assetApi = {
  list: (estateId, params = {}) => axios.get(`${API}/estates/${estateId}/assets`, { params }),
  get: (estateId, assetId) => axios.get(`${API}/estates/${estateId}/assets/${assetId}`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/assets`, data),
  update: (estateId, assetId, data) => axios.put(`${API}/estates/${estateId}/assets/${assetId}`, data),
  delete: (estateId, assetId) => axios.delete(`${API}/estates/${estateId}/assets/${assetId}`)
};

// Heir API
export const heirApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/heirs`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/heirs`, data),
  update: (estateId, heirId, data) => axios.put(`${API}/estates/${estateId}/heirs/${heirId}`, data),
  delete: (estateId, heirId) => axios.delete(`${API}/estates/${estateId}/heirs/${heirId}`)
};

// Debt API
export const debtApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/debts`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/debts`, data),
  update: (estateId, debtId, data) => axios.put(`${API}/estates/${estateId}/debts/${debtId}`, data),
  delete: (estateId, debtId) => axios.delete(`${API}/estates/${estateId}/debts/${debtId}`)
};

// Transaction (Cashflow) API
export const transactionApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/transactions`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/transactions`, data),
  update: (estateId, txnId, data) => axios.put(`${API}/estates/${estateId}/transactions/${txnId}`, data),
  delete: (estateId, txnId) => axios.delete(`${API}/estates/${estateId}/transactions/${txnId}`),
  bulkImport: (estateId, transactions) => axios.post(`${API}/estates/${estateId}/transactions/bulk`, transactions),
  bulkUpdate: (estateId, data) => axios.put(`${API}/estates/${estateId}/transactions/bulk-update`, data),
  bulkDelete: (estateId, transactionIds) => axios.delete(`${API}/estates/${estateId}/transactions/bulk-delete`, { data: transactionIds }),
  // Asset sale allocation for lump-sum sales
  updateAllocations: (estateId, txnId, data) => axios.post(`${API}/estates/${estateId}/transactions/${txnId}/allocate`, data)
};

// Executor Time API
export const executorTimeApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/executor-time`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/executor-time`, data),
  delete: (estateId, entryId) => axios.delete(`${API}/estates/${estateId}/executor-time/${entryId}`)
};

// Distribution API
export const distributionApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/distributions`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/distributions`, data),
  update: (estateId, distId, data) => axios.put(`${API}/estates/${estateId}/distributions/${distId}`, data),
  delete: (estateId, distId) => axios.delete(`${API}/estates/${estateId}/distributions/${distId}`)
};

// Contact API
export const contactApi = {
  submit: (data) => axios.post(`${API}/contact`, data)
};

// Executor Contact API
export const executorContactApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/contacts`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/contacts`, data),
  update: (estateId, contactId, data) => axios.put(`${API}/estates/${estateId}/contacts/${contactId}`, data),
  delete: (estateId, contactId) => axios.delete(`${API}/estates/${estateId}/contacts/${contactId}`)
};

// Executor Expense API
export const executorExpenseApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/expenses`),
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/expenses`, data),
  update: (estateId, expenseId, data) => axios.put(`${API}/estates/${estateId}/expenses/${expenseId}`, data),
  delete: (estateId, expenseId) => axios.delete(`${API}/estates/${estateId}/expenses/${expenseId}`),
  uploadAttachment: (estateId, expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/estates/${estateId}/expenses/${expenseId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (estateId, expenseId, attachmentId) => 
    axios.delete(`${API}/estates/${estateId}/expenses/${expenseId}/attachments/${attachmentId}`)
};

// Decedent Document API
export const documentApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/documents`),
  upload: (estateId, formData) => axios.post(`${API}/estates/${estateId}/documents/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (estateId, docId, data) => axios.put(`${API}/estates/${estateId}/documents/${docId}`, data),
  delete: (estateId, docId) => axios.delete(`${API}/estates/${estateId}/documents/${docId}`),
  getFileUrl: (estateId, filename) => `${API}/estates/${estateId}/documents/file/${filename}`,
  // Download file with authentication
  downloadFile: async (estateId, filename) => {
    const response = await axios.get(`${API}/estates/${estateId}/documents/file/${filename}`, {
      responseType: 'blob'
    });
    return response;
  }
};

// Collaborator API
export const collaboratorApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/collaborators`),
  invite: (estateId, data) => axios.post(`${API}/estates/${estateId}/collaborators`, data),
  update: (estateId, collaboratorId, data) => axios.put(`${API}/estates/${estateId}/collaborators/${collaboratorId}`, data),
  remove: (estateId, collaboratorId) => axios.delete(`${API}/estates/${estateId}/collaborators/${collaboratorId}`)
};

// Invitation API
export const invitationApi = {
  getDetails: (token) => axios.get(`${API}/invitations/${token}`),
  accept: (token) => axios.post(`${API}/invitations/${token}/accept`),
  decline: (token) => axios.post(`${API}/invitations/${token}/decline`)
};

// Activity Log API
export const activityApi = {
  list: (estateId, params = {}) => axios.get(`${API}/estates/${estateId}/activity`, { params })
};

// Transaction Attachment API
export const attachmentApi = {
  upload: (estateId, transactionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/estates/${estateId}/transactions/${transactionId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (estateId, transactionId) => axios.get(`${API}/estates/${estateId}/transactions/${transactionId}/attachments`),
  delete: (estateId, transactionId, attachmentId) => axios.delete(`${API}/estates/${estateId}/transactions/${transactionId}/attachments/${attachmentId}`)
};

// Debt Attachment API (forgiveness letters & court documents)
export const debtAttachmentApi = {
  upload: (estateId, debtId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/estates/${estateId}/debts/${debtId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (estateId, debtId) => axios.get(`${API}/estates/${estateId}/debts/${debtId}/attachments`),
  delete: (estateId, debtId, attachmentId) => axios.delete(`${API}/estates/${estateId}/debts/${debtId}/attachments/${attachmentId}`)
};

// Notification API
export const notificationApi = {
  sendTaskReminders: (estateId) => axios.post(`${API}/notifications/send-task-reminders/${estateId}`),
  previewTaskReminder: (estateId) => axios.get(`${API}/notifications/preview-task-reminder/${estateId}`),
  notifyDocumentUpload: (estateId, documentName) => 
    axios.post(`${API}/notifications/notify-document-upload/${estateId}?document_name=${encodeURIComponent(documentName)}`)
};

// Estate Summary API (for real-time dashboard data)
export const summaryApi = {
  get: (estateId) => axios.get(`${API}/estates/${estateId}/summary`)
};

// Subcategory API (user-defined subcategories)
export const subcategoryApi = {
  list: (estateId, parentCategory = null) => {
    const params = parentCategory ? { parent_category: parentCategory } : {};
    return axios.get(`${API}/estates/${estateId}/subcategories`, { params });
  },
  create: (estateId, data) => axios.post(`${API}/estates/${estateId}/subcategories`, data),
  delete: (estateId, subcatId) => axios.delete(`${API}/estates/${estateId}/subcategories/${subcatId}`),
  getStructure: (estateId) => axios.get(`${API}/estates/${estateId}/subcategories/categories`)
};

// State Guidance API (50 US states + DC probate guidance)
export const stateGuidanceApi = {
  listStates: () => axios.get(`${API}/states`),
  getState: (code) => axios.get(`${API}/states/${code}`),
  getEstateGuidance: (estateId) => axios.get(`${API}/estates/${estateId}/state-guidance`),
  applyStateTasks: (estateId) => axios.post(`${API}/estates/${estateId}/apply-state-tasks`)
};


// Subscription API
export const subscriptionApi = {
  getCurrent: () => axios.get(`${API}/subscriptions/current`),
  checkout: (plan, couponCode) => axios.post(`${API}/subscriptions/checkout`, {
    plan,
    origin_url: window.location.origin,
    coupon_code: couponCode || undefined,
  }),
};

// Password reset API
export const passwordResetApi = {
  request: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  confirm: (token, newPassword) => axios.post(`${API}/auth/reset-password`, {
    token,
    new_password: newPassword,
  }),
};

// Admin API (gated server-side; 404 for non-admins)
export const adminApi = {
  check: () => axios.get(`${API}/admin/check`),
  stats: () => axios.get(`${API}/admin/stats`),
  leads: (params = {}) => axios.get(`${API}/admin/leads`, { params }),
  exportLeadsCsvUrl: () => `${API}/admin/leads/export.csv`,
  deleteLead: (id) => axios.delete(`${API}/admin/leads/${id}`),
  broadcastPreview: (state_code) => axios.get(`${API}/admin/leads/broadcast-preview`, { params: state_code ? { state_code } : {} }),
  broadcastSend: (body) => axios.post(`${API}/admin/leads/broadcast`, body),
  broadcastsHistory: () => axios.get(`${API}/admin/leads/broadcasts`),
  users: (params = {}) => axios.get(`${API}/admin/users`, { params }),
  extendTrial: (id, days) => axios.post(`${API}/admin/users/${id}/extend-trial`, { days }),
  markPaid: (id, body) => axios.post(`${API}/admin/users/${id}/mark-paid`, body),
  deleteUser: (id) => axios.delete(`${API}/admin/users/${id}`),
  payments: (params = {}) => axios.get(`${API}/admin/payments`, { params }),
  refund: (id) => axios.post(`${API}/admin/payments/${id}/refund`),
  systemHealth: () => axios.get(`${API}/admin/system-health`),
};

export const firmBrandingApi = {
  get: () => axios.get(`${API}/users/firm-branding`),
  update: (formData) => axios.put(`${API}/users/firm-branding`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteLogo: () => axios.delete(`${API}/users/firm-branding/logo`),
};

export const shareLinkApi = {
  list: (estateId) => axios.get(`${API}/estates/${estateId}/share-links`),
  create: (estateId, body) => axios.post(`${API}/estates/${estateId}/share-links`, body),
  revoke: (estateId, linkId) => axios.delete(`${API}/estates/${estateId}/share-links/${linkId}`),
};

export const publicEstateViewApi = {
  meta: (token) => axios.get(`${API}/public/estate-view/${token}/meta`),
  verify: (token, password) => axios.post(`${API}/public/estate-view/${token}/verify`, { password }),
  get: (token, password) => axios.get(`${API}/public/estate-view/${token}`, {
    headers: password ? { 'X-View-Password': password } : {},
  }),
};

export const integrationRequestApi = {
  submit: (body) => axios.post(`${API}/integration-requests`, body),
};

export const outreachDripApi = {
  stats: (audience) => axios.get(`${API}/admin/outreach/${audience}/stats`),
  list: (audience, params) => axios.get(`${API}/admin/outreach/${audience}`, { params }),
  add: (audience, body) => axios.post(`${API}/admin/outreach/${audience}`, body),
  importCsv: (audience, formData) => axios.post(`${API}/admin/outreach/${audience}/import-csv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateStatus: (audience, id, status) => axios.patch(`${API}/admin/outreach/${audience}/${id}`, { status }),
  remove: (audience, id) => axios.delete(`${API}/admin/outreach/${audience}/${id}`),
};

